import { Link } from "wouter";
import { Building2, ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/seo-head";

const TERMS_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions",
    "description": "The Proper Tool terms and conditions: subscription plans, acceptable use, AI disclaimer, and governing law.",
    "url": "https://thepropertool.com/terms",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Terms & Conditions",
        "item": "https://thepropertool.com/terms",
      },
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Terms and Conditions"
        description="The Proper Tool terms and conditions: subscription plans, acceptable use, AI disclaimer, and governing law."
        canonical="/terms"
        schema={TERMS_SCHEMA}
      />
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">The Proper Tool</span>
            </div>
          </Link>
          <Link href="/">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Terms and Conditions</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: June 2026</p>

        <div className="prose prose-sm max-w-none space-y-8 text-foreground">

          <section>
            <h2 className="text-xl font-semibold mb-3">1. About These Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms and Conditions govern your use of The Proper Tool, a subscription-based software service for construction quoting, operated by The Proper Tool Ltd ("we", "us", "our"). By registering for an account or using the service, you agree to be bound by these terms. If you do not agree, please do not use the platform.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              These terms are governed by the laws of England and Wales. If you are accessing the service as a business, you confirm you have the authority to bind that business to these terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. The Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool provides AI-assisted construction quoting software designed for builders and construction firms. The platform allows you to generate, manage and export quotes, maintain a cost library, and track pricing intelligence based on your historical projects. You can also store and manage client records, share quotes with clients via a secure public link, and upload project documents or site photographs for AI-assisted analysis. The platform is available to builders in the United Kingdom, Australia, Canada, and the United States, with pricing and terminology localised for each market.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to update, modify or discontinue features of the service at any time. We will give reasonable notice of any material changes where possible.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To maintain and improve the quality of the service, authorised members of our team may periodically review quote content, project descriptions, uploaded files and AI-generated outputs. This activity is conducted solely for internal purposes including quality control, AI accuracy improvement, debugging, and product development. Any such review is carried out in accordance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Account Registration</h2>
            <p className="text-muted-foreground leading-relaxed">
              You must register for an account to use the service. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You must provide accurate and up-to-date information during registration.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              You must be at least 18 years old and using the service for business purposes. The platform is intended for trade professionals and is not a consumer service.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to suspend or terminate accounts that breach these terms, provide false information, or engage in fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Free Trial</h2>
            <p className="text-muted-foreground leading-relaxed">
              New accounts receive a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. No payment details are required to start a trial. Changes to the trial duration apply only to newly registered accounts; existing trial expiry dates and previously granted extensions are preserved. At the end of the trial period, or once the 5-quote allowance has been used, you must subscribe to a paid plan to continue generating quotes. We reserve the right to modify or withdraw trial terms at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Subscription Plans and Billing</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool is offered on a monthly subscription basis. Three plans are available:
            </p>
            <div className="mt-3 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left px-4 py-3 font-medium text-foreground">Plan</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground">Price</th>
                    <th className="text-left px-4 py-3 font-medium text-foreground">Auto Quote Limit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Lite</td>
                    <td className="px-4 py-3 text-muted-foreground">£29/month + VAT</td>
                    <td className="px-4 py-3 text-muted-foreground">10 Auto quotes/month · Unlimited Template &amp; Manual</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Starter</td>
                    <td className="px-4 py-3 text-muted-foreground">£59/month + VAT</td>
                    <td className="px-4 py-3 text-muted-foreground">25 Auto quotes/month · Unlimited Template &amp; Manual</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground">Pro</td>
                    <td className="px-4 py-3 text-muted-foreground">£99/month + VAT</td>
                    <td className="px-4 py-3 text-muted-foreground">50 Auto quotes/month · Unlimited Template &amp; Manual</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Monthly quotas apply to Auto (AI) quotes only. Lite allows up to 10 Auto quotes per calendar month, Starter up to 25, and Pro up to 50. Once your Auto quote limit is reached, you will need to upgrade your plan or wait until the following month. Template and Manual quotes are unlimited on all paid plans.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Subscriptions renew automatically each month on the date you first subscribed. Payment is taken in advance at the start of each billing period. All payments are processed securely by Stripe. We do not store your card details.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              If a subscription payment fails, access to the platform will be suspended until the outstanding payment is resolved. You will be notified by email and given the opportunity to update your payment details.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to change subscription prices. We will give you at least 30 days notice before any price increase takes effect. If you do not wish to continue at the new price, you may cancel before the increase applies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Cancellation and Refunds</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may cancel your subscription at any time through the Settings page or by contacting us. Cancellation takes effect at the end of your current billing period. You will continue to have access to the service until that date.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              As this is a business-to-business service for professional use, the 14-day statutory cancellation right under the Consumer Contracts Regulations does not typically apply. However, if you cancel within 48 hours of your first paid subscription and have not materially used the service, we will consider refund requests on a case-by-case basis.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We do not issue refunds for partial months or for periods where the service was available but not used.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. AI-Generated Content Disclaimer</h2>
            <p className="text-muted-foreground leading-relaxed">
              The platform uses artificial intelligence to generate construction quote estimates. All AI-generated content is provided as a starting point and is intended to assist, not replace, your professional judgement.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Quotes, cost estimates and pricing suggestions produced by the platform are estimates only. They do not constitute professional surveying, engineering or architectural advice. You are solely responsible for reviewing, verifying and taking responsibility for any quotes you issue to your clients. We accept no liability for quotes that are inaccurate, incomplete or that result in financial loss.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Material costs, labour rates and regional pricing data may not reflect current market conditions. Always verify costs with your suppliers and subcontractors before issuing final quotes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1.5 text-muted-foreground">
              <li>Use the service for any unlawful purpose or in breach of any applicable law or regulation</li>
              <li>Attempt to gain unauthorised access to the platform, its servers or connected systems</li>
              <li>Use automated tools or scripts to scrape, extract or misuse data from the platform</li>
              <li>Share your account credentials with others or allow multiple individuals to use a single account</li>
              <li>Submit false, misleading or fraudulent information through the platform</li>
              <li>Resell or sublicense access to the service without our prior written consent</li>
              <li>Reverse engineer, decompile or attempt to extract source code from the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Referral Programme</h2>
            <p className="text-muted-foreground leading-relaxed">
              We operate a referral programme that allows existing subscribers to earn fixed monthly rewards when they refer new paying subscribers. Referral rewards are: £10 per month for a Lite referral, £15 per month for Starter, and £20 per month for Pro.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Rewards are earned only while the referred subscriber's account is active and paid. Rewards stop if the referred user cancels or their subscription lapses. Referral rewards are valid for up to 12 months from the date the referred subscriber activates.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We reserve the right to withhold or reverse rewards where we suspect fraud, abuse or manipulation of the referral system. The referral programme may be changed or withdrawn at any time. Changes will not affect rewards already accrued.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We may also operate authorised partner programmes with third-party organisations. Where a partner programme is active, users who register via a qualifying partner link may receive a unique discount code generated automatically upon subscription activation. Partner codes are personal to the account holder, non-transferable, and subject to the specific terms of the relevant partner programme. We may change or withdraw any partner programme at any time. Existing codes that have already been issued remain valid subject to their original conditions unless we notify you otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Uploaded Content and AI Processing</h2>
            <p className="text-muted-foreground leading-relaxed">
              The platform allows you to upload files including construction drawings, plans, PDFs, and site photographs. These files are processed by AI to assist in generating quote estimates.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Uploaded files are used solely for the purposes of providing the service to you. They may be temporarily retained in secure storage to complete processing and to support quality review and debugging as described in Section 2. Uploaded content is not shared with third parties except as set out in our Privacy Policy.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              You must only upload content that you own, have created, or have the right to use for this purpose. You must not upload content that infringes any third-party intellectual property rights, contains personal data beyond what is reasonably necessary for quoting purposes, or is otherwise unlawful. By uploading content, you confirm that you have the necessary rights to do so.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Proper Tool and all associated software, design, content and trademarks are owned by or licensed to us. You are granted a limited, non-exclusive licence to use the platform for your own internal business purposes during your subscription.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              You retain ownership of the data and content you input into the platform, including project details, client information, and any uploaded documents or files. By using the service, you grant us a limited, non-exclusive licence to process your data and uploaded content for the following purposes: delivering the service, generating AI-assisted quotes, internal quality review and testing, identifying and resolving technical issues, improving AI accuracy, and developing new features. This licence does not permit us to sell your data or share it with third parties except as described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the fullest extent permitted by law, The Proper Tool Ltd shall not be liable for any indirect, incidental, special, consequential or punitive damages arising from your use of the service, including but not limited to loss of profits, contracts, data or business opportunity.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Our total aggregate liability to you in respect of any claim arising from or in connection with these terms shall not exceed the total subscription fees paid by you in the three months preceding the claim.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Nothing in these terms limits our liability for death or personal injury caused by our negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Service Availability</h2>
            <p className="text-muted-foreground leading-relaxed">
              We aim to maintain high availability but cannot guarantee that the service will be uninterrupted or error-free. We may carry out maintenance that temporarily affects availability and will aim to do so outside normal business hours where practicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Changes to These Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms from time to time. We will notify you by email or in-app notification before material changes take effect. Your continued use of the service after that date constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">15. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising under or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">16. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these terms, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-muted rounded-lg text-muted-foreground text-sm">
              <p className="font-medium text-foreground">The Proper Tool</p>
              <p>Email: hello@thepropertool.com</p>
              <p>Website: thepropertool.com</p>
            </div>
          </section>

        </div>
      </main>

      <footer className="border-t mt-16 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} The Proper Tool. All rights reserved.</span>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link href="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms and Conditions</Link>
            <Link href="/referral-terms" className="hover:text-foreground transition-colors">Referral Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
